/* eslint-disable no-nested-ternary */
import { gmbListReviews } from 'src/services/googleMyBusiness';

// ──────────────────────────────────────────────────────────────────────────────
// fetchAllNeedsResponse — shared pagination loop for the "needs response"
// (unreplied) Google reviews list.
//
// Previously duplicated in two places:
//  - src/redux/slices/reviewStatsCache.js prefetchGoogleReviewsOnDashboardLoad
//  - src/pages/dashboard/ManageBusiness.jsx loadNeedsResponse
//
// Both looped the same way (first page → count → remaining pages fetched in
// parallel, capped), which let the 1,000-row cap and the completeness
// computation drift between the two call sites. Extracted into one helper so
// there is exactly one implementation of "what does complete mean" and "how
// many rows can this fetch".
//
// Callers:
//  - reviewStatsCache.js's dashboard-load prefetch calls this with
//    { pageSize: 5, maxPages: 1 } — it only needs the launchpad's top-5
//    preview plus the server's true count, not the full list.
//  - ManageBusiness.jsx's loadNeedsResponse calls this with the defaults —
//    the full reviews page needs every unreplied review.
//
// Row cap: with the default pageSize=50 and maxPages=20, at most
// 1 + 19 * 50 = 1,000 rows are fetched (1 first-page request + up to 19
// follow-up requests, run in parallel). This bounds the worst-case fan-out
// for a location with a very large number of unreplied reviews; `complete`
// tells the caller whether the cap was hit.
// ──────────────────────────────────────────────────────────────────────────────

/**
 * @param {number|string} practiceId
 * @param {number|string} accountId
 * @param {number|string} locationId
 * @param {{ pageSize?: number, maxPages?: number }} [options]
 * @returns {Promise<{ data: Array, count: number, complete: boolean, loadedPages: number }>}
 */
export async function fetchAllNeedsResponse(practiceId, accountId, locationId, options = {}) {
    const { pageSize = 50, maxPages = 20 } = options;

    if (!practiceId || !accountId || !locationId) {
        return { data: [], count: 0, complete: true, loadedPages: 0 };
    }

    const firstParams = {
        limit: pageSize,
        offset: 0,
        reply__isnull: 'true',
        ordering: '-create_time',
    };
    const firstResponse = await gmbListReviews(practiceId, accountId, locationId, firstParams);
    const firstBatch = Array.isArray(firstResponse?.results)
        ? firstResponse.results
        : Array.isArray(firstResponse) ? firstResponse : [];
    const totalCount = typeof firstResponse?.count === 'number' ? firstResponse.count : firstBatch.length;

    let allResults = [...firstBatch];
    let loadedPages = 1;

    // Only escalate to more pages when the first page was full (more to fetch),
    // the server reports more than we already have, and the caller allows it
    // (maxPages > 1 — a prefetch with maxPages: 1 never escalates).
    if (maxPages > 1 && totalCount > pageSize && firstBatch.length === pageSize) {
        const remainingPages = Math.ceil((totalCount - pageSize) / pageSize);
        const pagesToFetch = Math.min(remainingPages, maxPages - 1);

        const pagePromises = Array.from({ length: pagesToFetch }, (_, i) => {
            const offset = (i + 1) * pageSize;
            return gmbListReviews(practiceId, accountId, locationId, {
                limit: pageSize,
                offset,
                reply__isnull: 'true',
                ordering: '-create_time',
            }).catch(() => ({ results: [] }));
        });

        const pageResponses = await Promise.all(pagePromises);
        pageResponses.forEach((resp) => {
            const batch = Array.isArray(resp?.results) ? resp.results : Array.isArray(resp) ? resp : [];
            allResults = [...allResults, ...batch];
        });
        loadedPages += pagesToFetch;
    }

    return {
        data: allResults,
        count: totalCount,
        complete: allResults.length >= totalCount,
        loadedPages,
    };
}
